import { props, map, pathOr } from 'ramda'
import URLSearchParams from 'url-search-params'

import {
  BODY_NO_SCROLL_CLASS,
  BODY_NO_SCROLL_MOBILE_CLASS,
  BUTTON_OFF,
  BUTTON_ON,
  SEARCH_FILTERS_FORM,
  SEARCH_PARAM_DATE_END,
  SEARCH_PARAM_DATE_START,
  SEARCH_PARAM_APPARTMENT,
  SEARCH_PARAM_CLIENT_CODE,
  SEARCH_PARAM_IATA_CODE,
  SEARCH_PARAM_PROMO_CODE,
} from '../constants'
import {generate} from "shortid";

const oneDayBooking = pathOr(null, ['drupalSettings', 'oneDayBooking'], window)

const formatMonth = (date, locale) => {
  const formatter = new Intl.DateTimeFormat(locale, { month: 'short' })
  const month = formatter.format(new Date(date)).toLocaleLowerCase()
  return month[month.length - 1] !== '.' ? `${month}.` : month
}

/**
 * Selected period for Dates Filter
 *
 * @param dates<Array>
 * @param fromLabel<String>
 * @param toLabel<String>
 * @param locale<String>
 * @returns any
 */
export const selectedPeriod = (dates, fromLabel, toLabel, locale) => {
  let from = ''
  let to = ''

  if (Array.isArray(dates)) {
    const dateFrom = new Date(dates[0])
    from = `${dateFrom.getDate()} ${formatMonth(dateFrom, locale)}`

    if (dates.length === 2 && dates[1]) {
      const dateTo = new Date(dates[1])
      to = `${dateTo.getDate()} ${formatMonth(dateTo, locale)}`
    }
  }

  const rangeBookingMarkup = `
      <span class="period-wrap">
        <span class="period-label">${fromLabel}</span>
        <span class="period start-day">${from}</span>
      </span>
      <span class="period-divider"></span>
      <span class="period-wrap">
        <span class="period-label">${toLabel}</span>
        <span class="period end-day">${to}</span>
      </span>
    `

  const oneDayBookingMarkup = `<span class="period start-day one-day-booking">${from}</span>`

  return oneDayBooking ? oneDayBookingMarkup : rangeBookingMarkup
}

export const filledRooms = rooms => (
  rooms
    ? rooms.filter(room => (
      (room.adults + room.children + room.babies) > 0
    ))
    : []
)

export const countRooms = rooms => (
  filledRooms(rooms).length
)

const countGuests = (rooms, guest) => {
  let guests = 0
  filledRooms(rooms)
    .filter(room => room[guest] > 0)
    .forEach((room) => { guests += room[guest] })
  return guests
}

export const selectedRooms = (
  roomsArray,
  {
    adult,
    adults,
    child,
    children,
    baby,
    babies,
    room,
    rooms,
  },
  defaultLabel,
) => {
  const roomsLabel = []
  const roomsCount = countRooms(roomsArray)
  if (roomsCount > 0) {
    const adultsCount = countGuests(roomsArray, 'adults')
    if (adultsCount > 0) {
      roomsLabel.push(`${adultsCount} ${adultsCount > 1 ? adults : adult}`)
    }

    const childrenCount = countGuests(roomsArray, 'children')
    if (childrenCount > 0) {
      roomsLabel.push(`${childrenCount} ${childrenCount > 1 ? children : child}`)
    }

    const babiesCount = countGuests(roomsArray, 'babies')
    if (babiesCount > 0) {
      roomsLabel.push(`${babiesCount} ${babiesCount > 1 ? babies : baby}`)
    }

    roomsLabel.push(`${roomsCount} ${roomsCount > 1 ? rooms : room}`)
  }
  return roomsLabel.length > 0 ? roomsLabel.join(' - ') : defaultLabel
}

export const getActivePrices = prices => (
  prices?.min || prices?.max
    ? [prices?.min, prices?.max]
    : []
)

export const getActiveAmenities = amenities => (
  amenities
    ? Object.entries(amenities).filter(value => value[1] === BUTTON_ON).map(id => id)
    : []
)

export const getActiveHotelType = type => type || BUTTON_OFF

export const selectedCode = (
  titles,
  defaultLabel,
) => {
  const label = []

  return label.length > 0 ? label.join(' - ') : defaultLabel
}

export const isMoreFiltersFieldsFilled = more => (
  getActivePrices(more.priceRange).length > 0
  || getActiveAmenities(more.amenities).length > 0
  || getActiveHotelType(more.hotelType) !== BUTTON_OFF
)

export const isCodeFiltersFieldFilled = values => (
  !!values && !!props(
    [SEARCH_PARAM_CLIENT_CODE,
      SEARCH_PARAM_IATA_CODE,
      SEARCH_PARAM_PROMO_CODE,
    ], map(item => (typeof item === 'string' ? item.trim() : item), values),
  ).find(value => !!value)
)

const formatUrlDate = date => `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, '0')}-${`${date.getDate()}`.padStart(2, '0')}`

export const URLDateParam = UTCDate => formatUrlDate(new Date(UTCDate))

export const dateFromURLParam = date => (
  new Date(date)
)

export const dateEndFromNights = (dateIn, nbNight) => {
  const dateStart = dateFromURLParam(dateIn)
  const nights = parseInt(nbNight, 10)
  dateStart.setDate(dateStart.getDate() + nights)
  return formatUrlDate(dateStart)
}

export const nightsFromPeriod = (period) => {
  const oneDay = (1000 * 60 * 60 * 24)
  if (period && period.length === 2) {
    const start = URLDateParam(period[0])
    const end = URLDateParam(period[1])
    const nightsNumber = ((new Date(end)).getTime() - (new Date(start)).getTime()) / oneDay

    return !oneDayBooking
      ? nightsNumber
      : nightsNumber > 0
        ? nightsNumber
        : 1
  }
  return 0
}

export const selectedSort = (item, titles) => (
  (item <= titles.length && item > 0) ? titles[item - 1] : ''
)

const bodyScroll = (hide = true, mobilesOnly) => {
  const bodyClassList = document.querySelector('body').classList
  const className = mobilesOnly ? BODY_NO_SCROLL_MOBILE_CLASS : BODY_NO_SCROLL_CLASS
  if (hide) {
    bodyClassList.add(className)
  } else {
    bodyClassList.remove(className)
  }
  return true
}

export const hideBodyScroll = (mobilesOnly = false) => bodyScroll(true, mobilesOnly)

export const showBodyScroll = (mobilesOnly = false) => bodyScroll(false, mobilesOnly)


export const getFiltersData = (state) => {
  const data = {}
  const {
    form: {
      searchFilters: {
        dates,
        datesApply,
        rooms,
        roomsApply,
      },
    },
  } = state

  if (dates && datesApply) {
    data[SEARCH_PARAM_DATE_START] = URLDateParam(dates[0])
    data[SEARCH_PARAM_DATE_END] = nightsFromPeriod(dates)
  }

  if (rooms && roomsApply) {
    const filledRoomsData = filledRooms(rooms)
      .map(r => ({
        adults: r.adults,
        children: r.children,
        babies: r.babies,
      }))

    const roomsArray = []

    filledRoomsData
      .forEach((room) => {
        roomsArray.push(`${Object.values(room).join(',')}`)
      })

    data[SEARCH_PARAM_APPARTMENT] = roomsArray
  }

  return data
}

export const updateDatesInSearchString = (link, dates) => {
  const [url, params] = link.split('?')
  const searchParams = new URLSearchParams(params)

  if (dates.length === 2) {
    searchParams.set('dateIn', URLDateParam(dates[0]))
    searchParams.set('nbNight', nightsFromPeriod(dates))
  }

  return [url, searchParams].join('?')
}

export const roomsDataToStr = (rooms) => {
  return filledRooms(rooms)
    .map(r => {
      const childrenAges = r.childrenAge?.length ? r.childrenAge.join('-') : 0
      const roomData = {
        adults: r.adults,
        children: `${r.children}:${childrenAges}`,
        babies: r.babies || '',
        id: r.id || '',
      }
      return Object.values(roomData).join(',')
    });
}

export const getFilledRooms = (state) => {
  const rooms = state?.searchFilters?.rooms || [];

  return roomsDataToStr(rooms)
}

export const updateDatesAndRoomsInSearchString = (link, dates, rooms) => {
  const [url, params] = link.split('?')
  const searchParams = new URLSearchParams(params)
  searchParams.delete(SEARCH_PARAM_APPARTMENT)

  if (dates.length === 2) {
    searchParams.set('dateIn', URLDateParam(dates[0]))
    searchParams.set('nbNight', nightsFromPeriod(dates))
  }
  // update rooms
  rooms.forEach((room, i) => {
    if (i === 0) searchParams.set('apartment[]', room)
    else searchParams.append('apartment[]', room)
  })

  return [url, searchParams].join('?')
}

export const updateRoomsInSearchString = (link, rooms) => {

  const [url, params] = link.split('?')
  const searchParams = new URLSearchParams(params)

  searchParams.delete(SEARCH_PARAM_APPARTMENT)

  rooms.forEach((room, i) => {
    if (i === 0) searchParams.set('apartment[]', room)
    else searchParams.append('apartment[]', room)
  })
  return [url, searchParams].join('?')
}
